/**
 * JITActiveSessionsPanel - Active JIT Sessions Dashboard
 *
 * FERP CRITICAL: Monitor and manage active temporary access sessions
 *
 * Features:
 * - Real-time session list with countdown timers
 * - Remaining time calculation
 * - Session extension capability
 * - Session revocation
 * - Emergency session highlighting
 * - Auto-refresh (10 seconds)
 * - Complete audit trail
 *
 * Architecture Principles:
 * - Data-driven (all sessions from backend)
 * - Repository pattern (JITAccessRepository)
 * - Real-time updates (network-only fetch)
 * - Immutable audit trails
 *
 * GitHub Issue: #343
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Table,
  Button,
  Alert,
  Group,
  Card,
  Badge,
  Loader,
  Modal,
  Textarea,
  NumberInput,
  ActionIcon,
  Tooltip,
  Box,
  Progress,
  RingProgress,
} from '@mantine/core';
import {
  IconClock,
  IconX,
  IconAlertCircle,
  IconUser,
  IconShield,
  IconRefresh,
  IconArrowsMaximize,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  JITAccessRepository,
  JITActiveSession,
  ExtendJITSessionInput,
  RevokeJITSessionInput,
  JITPolicy,
} from '../data/repositories/JITAccessRepository';

export interface JITActiveSessionsPanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Current user ID */
  userId: string;
  /** Show only current user's sessions */
  showOnlyMine?: boolean;
  /** Callback when session is extended */
  onExtend?: (sessionId: string) => void;
  /** Callback when session is revoked */
  onRevoke?: (sessionId: string) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

type ActionMode = 'extend' | 'revoke' | null;

/**
 * JIT Active Sessions Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (sessions from backend)
 * - ✅ Repository pattern (JITAccessRepository)
 * - ✅ Real-time countdown timers
 * - ✅ Immutable audit trails
 */
export function JITActiveSessionsPanel({
  tenantId,
  userId,
  showOnlyMine = false,
  onExtend,
  onRevoke,
  onError,
}: JITActiveSessionsPanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new JITAccessRepository(apolloClient), [apolloClient]);

  // State
  const [sessions, setSessions] = useState<JITActiveSession[]>([]);
  const [policy, setPolicy] = useState<JITPolicy | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Modal state
  const [selectedSession, setSelectedSession] = useState<JITActiveSession | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [actionReason, setActionReason] = useState<string>('');
  const [extensionMinutes, setExtensionMinutes] = useState<number>(30);
  const [submitting, setSubmitting] = useState<boolean>(false);

  /**
   * Load active sessions and policy
   */
  const loadSessions = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      // Load sessions
      const result = await repository.listActiveSessions(tenantId);
      let sessionList = result.sessions;

      // Filter to current user if requested
      if (showOnlyMine) {
        sessionList = sessionList.filter(s => s.userId === userId);
      }

      setSessions(sessionList);

      // Load policy (for extension limits)
      const policyData = await repository.getPolicy(tenantId);
      setPolicy(policyData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load sessions';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId, userId, showOnlyMine, repository, onError]);

  /**
   * Initial load
   */
  useEffect(() => {
    loadSessions(true);
  }, [loadSessions]);

  /**
   * Auto-refresh every 10 seconds
   */
  useEffect(() => {
    const interval = setInterval(() => {
      loadSessions(false);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [loadSessions]);

  /**
   * Update current time every second (for countdown)
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // 1 second

    return () => clearInterval(interval);
  }, []);

  /**
   * Calculate remaining time
   */
  const getRemainingTime = useCallback((expiresAt: string) => {
    const expiryDate = new Date(expiresAt);
    const diffMs = expiryDate.getTime() - currentTime.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs <= 0) {
      return { text: 'Expired', percentage: 0, color: 'red' };
    }

    const hours = Math.floor(diffSecs / 3600);
    const minutes = Math.floor((diffSecs % 3600) / 60);
    const seconds = diffSecs % 60;

    let text = '';
    if (hours > 0) {
      text = `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      text = `${minutes}m ${seconds}s`;
    } else {
      text = `${seconds}s`;
    }

    // Calculate percentage (based on original duration)
    const percentage = diffMs > 0 ? Math.min(100, Math.max(0, (diffSecs / (60 * 60 * 8)) * 100)) : 0;

    // Color based on remaining time
    let color = 'blue';
    if (diffSecs < 300) { // < 5 minutes
      color = 'red';
    } else if (diffSecs < 900) { // < 15 minutes
      color = 'orange';
    } else if (diffSecs < 1800) { // < 30 minutes
      color = 'yellow';
    }

    return { text, percentage, color };
  }, [currentTime]);

  /**
   * Open extension modal
   */
  const handleOpenExtend = useCallback((session: JITActiveSession) => {
    setSelectedSession(session);
    setActionMode('extend');
    setActionReason('');
    setExtensionMinutes(30);
  }, []);

  /**
   * Open revocation modal
   */
  const handleOpenRevoke = useCallback((session: JITActiveSession) => {
    setSelectedSession(session);
    setActionMode('revoke');
    setActionReason('');
  }, []);

  /**
   * Close modal
   */
  const handleCloseModal = useCallback(() => {
    setSelectedSession(null);
    setActionMode(null);
    setActionReason('');
    setExtensionMinutes(30);
  }, []);

  /**
   * Handle extend action
   */
  const handleExtend = useCallback(async () => {
    if (!selectedSession || !actionReason.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      const input: ExtendJITSessionInput = {
        tenantId,
        sessionId: selectedSession.sessionId,
        additionalMinutes: extensionMinutes,
        justification: actionReason.trim(),
      };

      await repository.extendSession(input);

      // Refresh list
      await loadSessions(false);

      // Close modal
      handleCloseModal();

      if (onExtend) {
        onExtend(selectedSession.sessionId);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to extend session';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedSession,
    actionReason,
    tenantId,
    extensionMinutes,
    repository,
    loadSessions,
    handleCloseModal,
    onExtend,
    onError,
  ]);

  /**
   * Handle revoke action
   */
  const handleRevoke = useCallback(async () => {
    if (!selectedSession) return;

    try {
      setSubmitting(true);
      setError(null);

      const input: RevokeJITSessionInput = {
        tenantId,
        sessionId: selectedSession.sessionId,
        revokedBy: userId,
        reason: actionReason.trim() || undefined,
      };

      await repository.revokeSession(input);

      // Refresh list
      await loadSessions(false);

      // Close modal
      handleCloseModal();

      if (onRevoke) {
        onRevoke(selectedSession.sessionId);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to revoke session';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedSession,
    actionReason,
    tenantId,
    userId,
    repository,
    loadSessions,
    handleCloseModal,
    onRevoke,
    onError,
  ]);

  /**
   * Format timestamp
   */
  const formatTimestamp = useCallback((timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  }, []);

  /**
   * Can extend session
   */
  const canExtend = useCallback((session: JITActiveSession) => {
    if (!policy || !policy.extensionEnabled) return false;
    return session.extensionCount < policy.maxExtensionCount;
  }, [policy]);

  // Loading state
  if (loading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading active sessions...</Text>
        </Group>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>Active JIT Sessions</Title>
            <Text size="sm" c="dimmed">
              {showOnlyMine ? 'Your active temporary access sessions' : 'All active temporary access sessions'}
            </Text>
          </div>
          <Group gap="xs">
            <Badge size="lg" variant="light">
              {sessions.length} Active
            </Badge>
            <Tooltip label="Refresh">
              <ActionIcon
                variant="light"
                onClick={() => loadSessions(false)}
                loading={refreshing}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Error message */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {/* Sessions table */}
        {sessions.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="gray">
            No active sessions found
          </Alert>
        ) : (
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Granted</Table.Th>
                  <Table.Th>Expires</Table.Th>
                  <Table.Th>Remaining</Table.Th>
                  <Table.Th>Extensions</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sessions.map((session) => {
                  const remaining = getRemainingTime(session.expiresAt);
                  return (
                    <Table.Tr key={session.sessionId}>
                      {/* User */}
                      <Table.Td>
                        <Group gap="xs">
                          <IconUser size={16} />
                          <div>
                            <Text size="sm" fw={500}>{session.userEmail}</Text>
                            <Text size="xs" c="dimmed">{session.userId}</Text>
                          </div>
                        </Group>
                      </Table.Td>

                      {/* Role */}
                      <Table.Td>
                        <Group gap="xs">
                          <IconShield size={16} />
                          <Text size="sm" fw={500}>
                            {session.roleDisplayName || session.roleName}
                          </Text>
                        </Group>
                      </Table.Td>

                      {/* Granted time */}
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatTimestamp(session.grantedAt)}
                        </Text>
                      </Table.Td>

                      {/* Expires time */}
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {formatTimestamp(session.expiresAt)}
                        </Text>
                      </Table.Td>

                      {/* Remaining time (countdown) */}
                      <Table.Td>
                        <Group gap="xs">
                          <RingProgress
                            size={40}
                            thickness={4}
                            sections={[{ value: remaining.percentage, color: remaining.color }]}
                          />
                          <div>
                            <Text size="sm" fw={500} c={remaining.color}>
                              {remaining.text}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {session.originalDurationMinutes} min total
                            </Text>
                          </div>
                        </Group>
                      </Table.Td>

                      {/* Extension count */}
                      <Table.Td>
                        <Badge variant="light" color={session.extensionCount > 0 ? 'blue' : 'gray'}>
                          {session.extensionCount}/{policy?.maxExtensionCount || 0}
                        </Badge>
                      </Table.Td>

                      {/* Actions */}
                      <Table.Td>
                        <Group gap="xs">
                          {canExtend(session) && (
                            <Tooltip label="Extend Session">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => handleOpenExtend(session)}
                              >
                                <IconArrowsMaximize size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Tooltip label="Revoke Session">
                            <ActionIcon
                              variant="light"
                              color="red"
                              onClick={() => handleOpenRevoke(session)}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {/* Extend Modal */}
        <Modal
          opened={actionMode === 'extend' && selectedSession !== null}
          onClose={handleCloseModal}
          title="Extend JIT Session"
          size="md"
        >
          {selectedSession && (
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Session Details</Text>
                  <Group gap="xs">
                    <Text size="sm">User:</Text>
                    <Text size="sm" fw={500}>{selectedSession.userEmail}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Role:</Text>
                    <Text size="sm" fw={500}>{selectedSession.roleName}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Current expiry:</Text>
                    <Text size="sm" fw={500}>{formatTimestamp(selectedSession.expiresAt)}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Extensions used:</Text>
                    <Text size="sm" fw={500}>
                      {selectedSession.extensionCount}/{policy?.maxExtensionCount || 0}
                    </Text>
                  </Group>
                </Stack>
              </Alert>

              <NumberInput
                label="Extension Duration (minutes)"
                placeholder="Enter extension duration"
                description="How many additional minutes to add to the session"
                value={extensionMinutes}
                onChange={(value: number | string) => setExtensionMinutes(typeof value === 'number' ? value : 30)}
                min={15}
                max={240}
                step={15}
                required
                leftSection={<IconClock size={16} />}
              />

              <Textarea
                label="Justification"
                placeholder="Explain why this session needs to be extended"
                description="Provide a clear business justification"
                value={actionReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setActionReason(e.currentTarget.value)}
                minRows={3}
                required
                error={
                  actionReason.length > 0 && actionReason.trim().length < 10
                    ? 'Minimum 10 characters required'
                    : undefined
                }
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="light" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  color="blue"
                  onClick={handleExtend}
                  loading={submitting}
                  disabled={actionReason.trim().length < 10}
                  leftSection={<IconArrowsMaximize size={16} />}
                >
                  Extend Session
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>

        {/* Revoke Modal */}
        <Modal
          opened={actionMode === 'revoke' && selectedSession !== null}
          onClose={handleCloseModal}
          title="Revoke JIT Session"
          size="md"
        >
          {selectedSession && (
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Session Details</Text>
                  <Group gap="xs">
                    <Text size="sm">User:</Text>
                    <Text size="sm" fw={500}>{selectedSession.userEmail}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Role:</Text>
                    <Text size="sm" fw={500}>{selectedSession.roleName}</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    This will immediately terminate the user&apos;s temporary access.
                  </Text>
                </Stack>
              </Alert>

              <Textarea
                label="Revocation Reason (optional)"
                placeholder="Optionally explain why this session is being revoked"
                value={actionReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setActionReason(e.currentTarget.value)}
                minRows={3}
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="light" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={handleRevoke}
                  loading={submitting}
                  leftSection={<IconX size={16} />}
                >
                  Revoke Session
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Card>
  );
}
